/**
 * Entwicklerschulung Prompts
 * 
 * Prompt-Templates für praktische Übungen und Aufgaben
 */

export const entwicklerschulungPrompts = {
  // Block 1: KI Fundamentals
  categorization: {
    title: '📝 KI-Systeme kategorisieren',
    description: 'Systematische Kategorisierung verschiedener KI-Systeme',
    prompt: `Erstelle eine Markdown-Datei und kategorisiere folgende Systeme:

**Systeme:**
- Netflix Recommendations
- GitHub Copilot
- Tesla Autopilot
- Google Translate
- DALL-E 2
- Spam Filter

**Kategorien:**
- Symbolic AI
- Machine Learning
- Deep Learning
- Generative AI

**Format:**
| System | Kategorie | Begründung |
|--------|-----------|-----------|
| ... | ... | ... |

**Hinweise:**
- Begründe jede Kategorisierung kurz (1-2 Sätze)
- Berücksichtige die zugrunde liegenden Technologien
- Mehrfachzuordnungen sind möglich`,
    tags: ['Übung', 'KI-Grundlagen', '10 Min']
  },

  llmCodeTasks: {
    title: '✏️ LLM für Code-Aufgaben nutzen',
    description: 'Praktische Coding-Challenges mit verschiedenen Schwierigkeitsgraden',
    prompt: `Nutzen Sie ChatGPT oder Claude für folgende Coding-Challenges:

**Challenge 1: Daten-Transformation** (⭐ Einsteiger)
- Array von User-Objekten (id, name, email, role)
- Gruppiere nach Role
- Sortiere nach Name innerhalb jeder Gruppe
- Gib als strukturiertes JSON aus

**Challenge 2: API-Client** (⭐⭐ Fortgeschritten)
- Erstelle TypeScript HTTP-Client
- Error Handling mit try/catch
- Retry-Logic (max 3 Versuche)
- Type-safe Responses mit Generics

**Challenge 3: LRU Cache** (⭐⭐⭐ Expert)
- LRU Cache implementieren
- O(1) für Get & Put Operationen
- TypeScript mit Generics
- Unit Tests mit Jest inklusive

**Bewertungskriterien:**
✓ Funktioniert der Code?
✓ Ist er gut lesbar?
✓ Sind Edge Cases abgedeckt?
✓ Ist er performant?

**Tipp:** Versuchen Sie verschiedene Prompt-Strategien und vergleichen Sie die Ergebnisse!`,
    tags: ['Coding', 'LLM', '15 Min']
  },

  promptImprovement: {
    title: '💭 Prompt Verbesserung',
    description: 'Schlechte Prompts in professionelle umwandeln',
    prompt: `**Aufgabe:** Verbessere diese schlechten Prompts zu professionellen Enterprise-Prompts

**Schlecht 1:** "Mach eine Login-Seite"

**Verbessert zu:**
"Erstelle eine responsive Login-Seite mit:
- React + TypeScript
- Formular mit Email & Passwort
- Client-seitige Validierung (Yup Schema)
- Error Handling & Loading States
- Tailwind CSS Styling
- Accessibility (ARIA labels, keyboard navigation)
- Tests mit React Testing Library"

---

**Jetzt Sie - verbessern Sie:**

**Schlecht 2:** "Schreib ne API"

**Verbessert zu:** [Ihre Lösung]

**Schlecht 3:** "Mach Tests"

**Verbessert zu:** [Ihre Lösung]

**Kriterien für gute Prompts:**
✓ Spezifische Technologien
✓ Klare Anforderungen
✓ Erwartete Qualitätsstandards
✓ Constraints & Einschränkungen`,
    tags: ['Prompt Engineering', 'Interaktiv', '15 Min']
  },

  // Block 2: AI Tools
  toolSetup: {
    title: '🛠️ AI-Tool Installation',
    description: 'Schritt-für-Schritt Setup Ihres bevorzugten AI-Coding-Tools',
    prompt: `**Schritte für Tool-Installation:**

**GitHub Copilot:**
1. VS Code öffnen
2. Extension installieren: "GitHub Copilot"
3. Mit GitHub Account anmelden
4. Copilot aktivieren (Status-Icon prüfen)
5. Test: Neue TypeScript-Datei → "function fibonacci" tippen

**Cursor:**
1. Download von cursor.sh
2. Installation ausführen
3. Workspace öffnen
4. Composer-Mode testen (Cmd/Ctrl+K)
5. Test: "Create a React component" im Composer

**Claude (Web/Desktop):**
1. Zu claude.ai navigieren
2. Anthropic Account erstellen
3. Artifacts-Feature aktivieren
4. Test: "Create a TypeScript interface for a user"

**Cline (VS Code Extension):**
1. VS Code Extension suchen: "Cline"
2. Installation
3. API Key konfigurieren
4. Test: "Create a new file with a function"

**Erfolgscheck:**
✓ Tool ist installiert
✓ Authentifizierung funktioniert
✓ Erste Code-Suggestion erhalten
✓ Interface verstanden`,
    tags: ['Setup', 'Tools', '15 Min']
  },

  // Block 3: Copilot Deep Dive
  ecommerceApi: {
    title: '🛠️ E-Commerce Product API',
    description: 'Vollständige API mit Node.js, Express und TypeScript',
    prompt: `**Workshop: E-Commerce Product API**

**Phase 1: Setup & Struktur** (10 Min)
\`\`\`bash
# Projekt initialisieren
npm init -y
npm install express typescript @types/express
npm install --save-dev nodemon ts-node

# tsconfig.json erstellen
npx tsc --init

# Ordnerstruktur
mkdir src
mkdir src/models src/routes src/controllers
\`\`\`

**Phase 2: Models & Routes** (15 Min)

Models erstellen:
- Product Interface (id, name, price, description, category, stock)
- In-Memory Datenbank (Array)
- CRUD Operationen

Routes implementieren:
- GET /api/products (alle Produkte)
- GET /api/products/:id (einzelnes Produkt)
- POST /api/products (neues Produkt)
- PUT /api/products/:id (Produkt aktualisieren)
- DELETE /api/products/:id (Produkt löschen)

**Phase 3: Validation & Testing** (15 Min)

Validierung hinzufügen:
- Zod Schema für Product
- Request Validation Middleware
- Error Handling

Tests schreiben:
- Supertest für API-Tests
- Happy Path Tests
- Error Cases

**Copilot Prompts:**
// Generate Product interface with TypeScript
// Create Express router for CRUD operations
// Add Zod validation schema
// Write supertest API tests`,
    tags: ['Workshop', 'API', '40 Min']
  },

  legacyModernization: {
    title: '🛠️ Legacy Code Modernisierung',
    description: 'Callback Hell → Async/Await & ES6+ Features',
    prompt: `**Workshop: Legacy Code Modernisierung**

**Teil 1: Callback Hell → Async/Await** (15 Min)

Legacy Code (Callbacks):
\`\`\`javascript
function getUser(id, callback) {
  db.query('SELECT * FROM users WHERE id = ?', [id], (err, user) => {
    if (err) return callback(err);
    db.query('SELECT * FROM orders WHERE user_id = ?', [id], (err, orders) => {
      if (err) return callback(err);
      callback(null, { user, orders });
    });
  });
}
\`\`\`

Moderner Code (Async/Await):
\`\`\`typescript
async function getUser(id: number): Promise<UserWithOrders> {
  const user = await db.query('SELECT * FROM users WHERE id = ?', [id]);
  const orders = await db.query('SELECT * FROM orders WHERE user_id = ?', [id]);
  return { user, orders };
}
\`\`\`

**Teil 2: ES6+ Features** (15 Min)

Modernisierungen:
- var → const/let
- function → Arrow Functions
- Object/Array Destructuring
- Spread & Rest Operators
- Template Literals
- Optional Chaining

**Copilot Mega-Prompt:**
// Modernize this code: ES6+, const/let, arrow functions, destructuring, async/await, TypeScript types`,
    tags: ['Refactoring', 'ES6+', '30 Min']
  },

  testCoverage: {
    title: '🛠️ Test Coverage erhöhen',
    description: 'Automatische Test-Generierung mit Copilot',
    prompt: `**Workshop: Test Coverage erhöhen**

**Ziel:** Von 0% auf 80%+ Test Coverage

**Phase 1: Unit Tests** (8 Min)
- Funktionen & Methoden testen
- Edge Cases abdecken
- Mocking von Dependencies

Copilot Prompts:
// Generate Jest unit tests for this function
// Add edge case tests (null, undefined, empty)
// Mock database calls

**Phase 2: Integration Tests** (8 Min)
- API Endpoints testen
- Datenbank-Interaktionen
- Error Handling

Copilot Prompts:
// Create supertest integration tests for this endpoint
// Test error scenarios (400, 401, 404, 500)
// Add authentication tests

**Phase 3: Coverage Report** (4 Min)
\`\`\`bash
# Jest Coverage
npm test -- --coverage

# Coverage Ziele
statements: > 80%
branches: > 75%
functions: > 80%
lines: > 80%
\`\`\`

**Best Practices:**
✓ AAA Pattern (Arrange, Act, Assert)
✓ Descriptive Test Names
✓ One Assertion per Test (wenn möglich)
✓ Mock External Dependencies`,
    tags: ['Testing', 'Jest', '20 Min']
  },

  // Context Engineering Integration
  contextEngineering: {
    title: '🎯 Context Engineering Grundlagen',
    description: 'Systematische KI-Entwicklung für bessere Ergebnisse',
    prompt: `**Context Engineering - Kernprinzipien**

**1. Klarheit:** Präzise Zieldefinition
- Was genau soll erreicht werden?
- Welche Erfolgskriterien gibt es?
- Welche Einschränkungen existieren?

**2. Struktur:** Systematischer Aufbau
- Logische Informationshierarchie
- Standardisierte Formate
- Wiederverwendbare Patterns

**3. Kontext:** Relevantes Informationsmanagement
- Technische Rahmenbedingungen
- Business-Anforderungen
- Historischer Kontext

**4. Iteration:** Kontinuierliche Optimierung
- Feedback-Schleifen
- Inkrementelle Verbesserungen
- Lernen aus Ergebnissen

**Prompt-Framework:**
\`\`\`
# ROLLE
[Definiere Expertise und Perspektive]

# KONTEXT
[Geschäftsziel, technischer Stack, Einschränkungen]

# ANFORDERUNGEN
[Funktionale & nicht-funktionale Anforderungen]

# LIEFERBARE
[Erwartete Outputs, Format, Qualitätskriterien]
\`\`\`

**Beispiel-Anwendung:**
Nutze dieses Framework für Ihre nächste AI-Anfrage und vergleiche die Ergebnisse mit einem einfachen Prompt!`,
    tags: ['Context Engineering', 'Best Practices', '15 Min']
  }
} as const

export default entwicklerschulungPrompts
